import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { DisposableStore, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { UntitledFileWorkingCopy } from './untitledFileWorkingCopy.js';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { IWorkingCopyService } from 'vscode/vscode/vs/workbench/services/workingCopy/common/workingCopyService.service';
import { ILabelService } from 'vscode/vscode/vs/platform/label/common/label.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IWorkingCopyBackupService } from 'vscode/vscode/vs/workbench/services/workingCopy/common/workingCopyBackup.service';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { BaseFileWorkingCopyManager } from './abstractFileWorkingCopyManager.js';
import { ResourceMap } from 'vscode/vscode/vs/base/common/map';

let UntitledFileWorkingCopyManager = class UntitledFileWorkingCopyManager extends BaseFileWorkingCopyManager {
    constructor(workingCopyTypeId, modelFactory, saveDelegate, fileService, labelService, logService, workingCopyBackupService, workingCopyService) {
        super(fileService, logService, workingCopyBackupService);
        this.workingCopyTypeId = workingCopyTypeId;
        this.modelFactory = modelFactory;
        this.saveDelegate = saveDelegate;
        this.labelService = labelService;
        this.workingCopyService = workingCopyService;
        this._onDidChangeDirty = this._register(( new Emitter()));
        this.onDidChangeDirty = this._onDidChangeDirty.event;
        this._onWillDispose = this._register(( new Emitter()));
        this.onWillDispose = this._onWillDispose.event;
        this.mapResourceToWorkingCopyListeners = ( new ResourceMap());
    }
    async resolve(options) {
        const workingCopy = this.doCreateOrGet(options);
        await workingCopy.resolve();
        return workingCopy;
    }
    doCreateOrGet(options = Object.create(null)) {
        const massagedOptions = this.massageOptions(options);
        if (massagedOptions.untitledResource) {
            const existingWorkingCopy = this.get(massagedOptions.untitledResource);
            if (existingWorkingCopy) {
                return existingWorkingCopy;
            }
        }
        return this.doCreate(massagedOptions);
    }
    massageOptions(options) {
        const massagedOptions = Object.create(null);
        if (options.associatedResource) {
            massagedOptions.untitledResource = ( URI.from({
                scheme: Schemas.untitled,
                authority: options.associatedResource.authority,
                fragment: options.associatedResource.fragment,
                path: options.associatedResource.path,
                query: options.associatedResource.query
            }));
            massagedOptions.associatedResource = options.associatedResource;
        }
        else {
            if (options.untitledResource?.scheme === Schemas.untitled) {
                massagedOptions.untitledResource = options.untitledResource;
            }
            massagedOptions.isScratchpad = options.isScratchpad;
        }
        massagedOptions.contents = options.contents;
        return massagedOptions;
    }
    doCreate(options) {
        let untitledResource = options.untitledResource;
        if (!untitledResource) {
            let counter = 1;
            do {
                untitledResource = ( URI.from({
                    scheme: Schemas.untitled,
                    path: options.isScratchpad ? `Scratchpad-${counter}` : `Untitled-${counter}`,
                    query: this.workingCopyTypeId ?
                        `typeId=${this.workingCopyTypeId}` :
                        undefined
                }));
                counter++;
            } while (( this.has(untitledResource)));
        }
        const workingCopy = ( new UntitledFileWorkingCopy(
            this.workingCopyTypeId,
            untitledResource,
            this.labelService.getUriBasenameLabel(untitledResource),
            !!options.associatedResource,
            !!options.isScratchpad,
            options.contents,
            this.modelFactory,
            this.saveDelegate,
            this.workingCopyService,
            this.workingCopyBackupService,
            this.logService
        ));
        this.registerWorkingCopy(workingCopy);
        return workingCopy;
    }
    registerWorkingCopy(workingCopy) {
        const workingCopyListeners = ( new DisposableStore());
        workingCopyListeners.add(workingCopy.onDidChangeDirty(() => this._onDidChangeDirty.fire(workingCopy)));
        workingCopyListeners.add(workingCopy.onWillDispose(() => this._onWillDispose.fire(workingCopy)));
        this.mapResourceToWorkingCopyListeners.set(workingCopy.resource, workingCopyListeners);
        this.add(workingCopy.resource, workingCopy);
        if (workingCopy.isDirty()) {
            this._onDidChangeDirty.fire(workingCopy);
        }
    }
    remove(resource) {
        const removed = super.remove(resource);
        const workingCopyListener = this.mapResourceToWorkingCopyListeners.get(resource);
        if (workingCopyListener) {
            dispose(workingCopyListener);
            this.mapResourceToWorkingCopyListeners.delete(resource);
        }
        return removed;
    }
    dispose() {
        super.dispose();
        dispose(( this.mapResourceToWorkingCopyListeners.values()));
        this.mapResourceToWorkingCopyListeners.clear();
    }
};
UntitledFileWorkingCopyManager = ( __decorate([
    ( __param(3, IFileService)),
    ( __param(4, ILabelService)),
    ( __param(5, ILogService)),
    ( __param(6, IWorkingCopyBackupService)),
    ( __param(7, IWorkingCopyService))
], UntitledFileWorkingCopyManager));

export { UntitledFileWorkingCopyManager };
